import React, { createContext, useState, useCallback, useRef } from 'react';
import { UserRole, ContextStatus, UserCompanyType, getBestUserCompanyType, EDegradedSubscriptionStatus } from '@leedpulse-monorepo/enums';
import { Authentication } from '@leedpulse-monorepo/classes';
import { useEffect } from 'react';
import 'firebase/auth';
import { Auth, getAuth, IdTokenResult, signInWithCustomToken, signInWithEmailAndPassword, User } from 'firebase/auth';
import {
  doc,
  getDoc,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import UserDoc from '../../../domain/models/UserDoc';
import updateUserProfileUseCase, {
  UpdateUserProfileRequest,
  UpdateUserProfileResponse,
} from '../../../domain/use_cases/users/updateUserProfile';
import sendPasswordResetEmailUseCase from '../../../domain/use_cases/auth/sendPasswordResetEmail';
import changeRequiredPasswordUseCase, {
  ChangeRequiredPasswordRequest,
  ChangeRequiredPasswordResponse,
  isPasswordChangeRequired as isPasswordChangeRequiredRule,
} from '../../../domain/use_cases/auth/changeRequiredPassword';
import { message } from 'antd';
import HttpClient from '../../../core/HttpClient';
import { ISubscription } from '@leedpulse-monorepo/interfaces';

export enum AuthenticationState {
  UNKNOWN,
  AUTHENTICATED,
  UNAUTHENTICATED,
}

export interface DegradedSubscription {
  id: string;
  status: EDegradedSubscriptionStatus;
}

interface UpdateProfileParams {
  firstname: string;
  lastname: string;
  avatar?: string;
}

export interface SendPasswordResetResponse {
  success: boolean;
  error?: string;
}

interface AuthenticationContextValue {
  authentication: Authentication | null;
  userDoc: UserDoc | null;
  status: ContextStatus;
  authenticationState: AuthenticationState;
  submitSignIn: (_: string, __: string) => Promise<void>;
  submitSignOut: () => Promise<void>;
  updateUserProfile: (_: UpdateProfileParams) => Promise<UpdateUserProfileResponse>;
  isUpdatingProfile: boolean;
  sendPasswordReset: () => Promise<SendPasswordResetResponse>;
  isSendingPasswordReset: boolean;
  isPasswordChangeRequired: boolean; // password sent by mail, never during a login as
  changeRequiredPassword: (_: ChangeRequiredPasswordRequest) => Promise<ChangeRequiredPasswordResponse>;
  getUserCompanyRole: (_: string) => UserCompanyType;
  degradedSubscriptions: DegradedSubscription[];
  setDegradedSubscriptions: (subs: DegradedSubscription[]) => void;
}

const AuthenticationContext = createContext<AuthenticationContextValue>({
  authentication: null as Authentication | null,
  userDoc: null as UserDoc | null,
  status: ContextStatus.IDLE,
  authenticationState: AuthenticationState.UNKNOWN,
  submitSignIn: (_: string, __: string) => new Promise<void>(() => null),
  submitSignOut: () => new Promise<void>(() => null),
  updateUserProfile: (_: UpdateProfileParams) => new Promise<UpdateUserProfileResponse>(() => null),
  isUpdatingProfile: false,
  sendPasswordReset: () => new Promise<SendPasswordResetResponse>(() => null),
  isSendingPasswordReset: false,
  isPasswordChangeRequired: false,
  changeRequiredPassword: (_: ChangeRequiredPasswordRequest) => new Promise<ChangeRequiredPasswordResponse>(() => null),
  getUserCompanyRole: (_: string) => UserCompanyType.SUPERVISOR as UserCompanyType,
  degradedSubscriptions: [],
  setDegradedSubscriptions: (_: DegradedSubscription[]) => undefined,
});

export const AuthenticationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [authentication, setAuthentication] = useState(null as Authentication | null);
  const [userDoc, setUserDoc] = useState(null as UserDoc | null);
  const [authenticationState, setAuthenticationState] = useState(AuthenticationState.UNKNOWN);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [degradedSubscriptions, setDegradedSubscriptions] = useState<DegradedSubscription[]>([]);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [hasChangedPassword, setHasChangedPassword] = useState(false);
  // Memoises the URL auth-code exchange so a remount (e.g. React StrictMode's
  // dev-only double effect invocation) awaits the same in-flight promise
  // instead of starting it again or registering listeners before it settles.
  const autoConnectRef = useRef<Promise<void> | null>(null);

  const unauthenticate = () => {
    setAuthentication(null);
    setUserDoc(null);
    setIsImpersonating(false);
    setHasChangedPassword(false);
    setDegradedSubscriptions([]);
    setAuthenticationState(AuthenticationState.UNAUTHENTICATED);
    setStatus(ContextStatus.SUCCESS);
  };

  const autoConnect = async (auth: Auth, authCode: string) => {
    try {
      // Check the validity of the authorization code
      const verifyResponse: any = await HttpClient.post(
        '/auth/verify',
        {
          authorization_code: authCode,
        },
        await auth.currentUser?.getIdToken() ?? ''
      );

      if (verifyResponse.success && verifyResponse.data?.valid) {
        // Sign in with the custom token
        await signInWithCustomToken(auth, authCode);

        // Clean the URL by removing the auth parameter
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        message.success('Connecté avec succès', 3);

        // dispatch({ type: type.AUTH_SUCCESS });
      } else {
        const errorMsg = "Code d'autorisation invalide";
        message.error(`Erreur d'auto-connexion: ${errorMsg}`, 4);
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('Auto-connect error:', error);

      // Handle the error
      let errorMsg;
      if (error.response && error.response.status === 401) {
        errorMsg = 'Token non valide ou expiré';
      } else if (error.message.includes('401')) {
        errorMsg = 'Token non valide ou expiré';
      } else {
        errorMsg = error.message || 'Erreur inconnue';
      }

      // setAutoConnectError(errorMsg);
      message.error(`Erreur d'auto-connexion: ${errorMsg}`, 4);
    }
  };

  const updateAuthenticationState = async (user: User) => {
    try {
      const frontendleedflowAllowedRoles = [
        UserRole.owner,
        UserRole.admin,
        UserRole.maintainer,
        UserRole.guest,
        UserRole.customer,
      ];

      const token = await user.getIdToken();
      const { uid, email } = user;
      const customClaims = await user
        .getIdTokenResult()
        .then((idTokenResult: IdTokenResult) => idTokenResult.claims);
      const role = customClaims.role as keyof typeof UserRole;

      if (!frontendleedflowAllowedRoles.includes(role as UserRole)) {
        setStatus(ContextStatus.ERROR);
        await getAuth().signOut();
        return;
      }

      const newAuthentication = new Authentication(uid, email ?? 'unknown', UserRole[role], token);

      setIsImpersonating(customClaims.impersonation === true);
      setAuthentication(newAuthentication);
      setAuthenticationState(AuthenticationState.AUTHENTICATED);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      console.error('error updating authentication state: ', e);
      unauthenticate();
    }
  };

  const isUserAuthorized = async (user: User): Promise<UserDoc | null> => {
    try {
      // Fetch user document from Firestore
      const userDocRef = await getDoc(doc(firestore, FirestoreCollections.users, user.uid));

      if (!userDocRef.exists()) {
        return null;
      }

      const fetchedUserDoc = UserDoc.fromFirestore(userDocRef);

      // if (fetchedUserDoc.email.endsWith('@leedpulse.com')) {
      //   return fetchedUserDoc;
      // }

      const companyIds = Object.keys(fetchedUserDoc.idCompanies || {});

      // If user has at least one company, they are authorized.
      // We check the IDs from the user doc directly — reading the company documents
      // is unnecessary here and can fail if the docs don't exist (Firestore rules
      // evaluate resource.data on a null resource → permission-denied → catch → null).
      if (companyIds.length > 0) return fetchedUserDoc;

      const subscriptionIds = Object.keys(fetchedUserDoc.idSubscription);

      if (!subscriptionIds || subscriptionIds.length === 0) {
        return null;
      }

      // Fetch all subscriptions in parallel
      const subscriptionPromises = subscriptionIds.map((subscriptionId: string) =>
        getDoc(doc(firestore, FirestoreCollections.subscriptions, subscriptionId))
      );

      const subscriptionDocs = await Promise.all(subscriptionPromises);

      // Convert documents to Subscription objects
      const subscriptions = subscriptionDocs
        .filter((doc: DocumentSnapshot<DocumentData>) => doc.exists())
        .map((doc: DocumentSnapshot<DocumentData>) =>
          doc.data() as ISubscription
        );

      // Check if user has at least one subscription with a plan starting with "lf-"
      const hasValidSubscription = subscriptions.some((subscription: ISubscription) =>
        subscription.id_plan.startsWith('lf-')
      );

      return hasValidSubscription ? fetchedUserDoc : null;
    } catch (e) {
      console.error('error fetching user companies or subscriptions: ', e);
      return null;
    }
  };

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeAuthState: (() => void) | undefined;
    let unsubscribeIdToken: (() => void) | undefined;
    let cancelled = false;

    const init = async () => {
      // Check if there is an authentication code in the URL. Await the (memoised)
      // auto-connect before registering listeners so that onAuthStateChanged fires
      // with the already-signed-in user.
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('auth');

      if (authCode) {
        autoConnectRef.current ??= autoConnect(auth, authCode);
        await autoConnectRef.current;
      }

      if (cancelled) return;

      unsubscribeAuthState = auth.onAuthStateChanged(async (user) => {
        const authorizedUserDoc = user ? await isUserAuthorized(user) : null;

        if (user && authorizedUserDoc) {
          setUserDoc(authorizedUserDoc);
          await updateAuthenticationState(user);
        } else {
          unauthenticate();
        }
      });

      unsubscribeIdToken = auth.onIdTokenChanged(async (user) => {
        const authorizedUserDoc = user ? await isUserAuthorized(user) : null;

        if (user && authorizedUserDoc) {
          setUserDoc(authorizedUserDoc);
          await updateAuthenticationState(user);
        } else {
          setAuthentication(null);
          setUserDoc(null);
          setAuthenticationState(AuthenticationState.UNAUTHENTICATED);
          setStatus(ContextStatus.SUCCESS);
        }
      });
    };

    init();

    return () => {
      cancelled = true;
      unsubscribeAuthState?.();
      unsubscribeIdToken?.();
    };
  }, []);

  // Function to auto-connect with an authorization code


  const submitSignIn = async (email: string, password: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      const userCredential = await signInWithEmailAndPassword(getAuth(), email, password);
      const user = userCredential.user;

      // Check if user is authorized (has subscription with plan starting with "lf-")
      const isAuthorized = await isUserAuthorized(user);

      if (!isAuthorized) {
        await getAuth().signOut();
        setStatus(ContextStatus.UNAUTHORIZED);
        return;
      }
    } catch (e) {
      console.error('error: ', e);
      setStatus(ContextStatus.ERROR);
    }
  };

  const submitSignOut = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      await getAuth().signOut();
    } catch (e) {
      console.error('error: ', e);
      setStatus(ContextStatus.ERROR);
    }
  };

  const updateUserProfile = useCallback(
    async (params: UpdateProfileParams): Promise<UpdateUserProfileResponse> => {
      if (!userDoc) {
        return { success: false, error: 'Utilisateur non connecté' };
      }

      setIsUpdatingProfile(true);

      const request: UpdateUserProfileRequest = {
        uid: userDoc.uid,
        firstname: params.firstname,
        lastname: params.lastname,
        ...(params.avatar && { avatar: params.avatar }),
      };

      const result = await updateUserProfileUseCase(request);

      if (result.success) {
        // Update local userDoc state with new values
        const updatedUserDoc = userDoc.copyWith({
          firstname: params.firstname,
          lastname: params.lastname,
          ...(params.avatar && { avatar: params.avatar }),
        });
        setUserDoc(updatedUserDoc);
      }

      setIsUpdatingProfile(false);
      return result;
    },
    [userDoc]
  );

  const sendPasswordReset = useCallback(async (): Promise<SendPasswordResetResponse> => {
    if (!userDoc?.email) {
      return { success: false, error: 'Utilisateur non connecté' };
    }

    setIsSendingPasswordReset(true);

    const result = await sendPasswordResetEmailUseCase({
      email: userDoc.email,
      firstname: userDoc.firstname,
    });

    setIsSendingPasswordReset(false);
    return result;
  }, [userDoc?.email, userDoc?.firstname]);

  const isPasswordChangeRequired = isPasswordChangeRequiredRule(userDoc?.passwordChangeRequiredAt, isImpersonating, hasChangedPassword);

  const changeRequiredPassword = useCallback(
    async (request: ChangeRequiredPasswordRequest): Promise<ChangeRequiredPasswordResponse> => {
      const result = await changeRequiredPasswordUseCase(request);
      if (result.success) {
        setHasChangedPassword(true);
      }
      return result;
    },
    []
  );

  const getUserCompanyRole = (companyId: string): UserCompanyType => {
    return getBestUserCompanyType(userDoc?.idCompanies?.[companyId]?.roles as UserCompanyType[] || []);
  };

  return (
    <AuthenticationContext.Provider
      value={{
        authentication,
        userDoc,
        status,
        authenticationState,
        submitSignIn,
        submitSignOut,
        updateUserProfile,
        isUpdatingProfile,
        sendPasswordReset,
        isSendingPasswordReset,
        isPasswordChangeRequired,
        changeRequiredPassword,
        getUserCompanyRole,
        degradedSubscriptions,
        setDegradedSubscriptions,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationContext;
